<!-- /* * Confidential and Proprietary. * Do not distribute without 1-800-Flowers.com, Inc. consent. *
Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved. */ -->
{#if config}
	<div
		class="modal-overlay"
		class:mobile-modal-overlay={$chatSession.deviceType === 'mobile'}
		data-testid="chat-widget-modal"
	>
		<div class="modal-content">
			<div class="modal-header-container">
				<p class="modal-header">{config.header}</p>
				{#if currentModalId !== 'start_api_error'}<button
						class="modal-close"
						on:click={handleCancel}
					>
						<img src="modal/x.svg" alt="close-icon" width="20" height="20" />
					</button>
				{/if}
			</div>
			<div class="modal-message">{config.message}</div>
			{#if isEmailModal}
				<div class="modal-input">
					<input type="text" bind:value={email} placeholder="Email address" />
				</div>
			{/if}
			<div class="button-container">
				{#if config?.secondaryLabel}
					<button class="cancel" on:click={handleCancel} data-testid="chat-widget-modal-cancel">
						{#if !isEmailModal && config?.secondaryIcon}
							<img src={config?.secondaryIcon} alt="action-icon" />
						{/if}
						{config?.secondaryLabel}
					</button>
				{/if}
				{#if config?.primaryLabel}
					<button
						class="confirm"
						on:click={handleConfirm}
						disabled={isEmailModal && (!email || !isEmailValid)}
						data-testid="chat-widget-modal-confirm"
					>
						{#if !isEmailModal && config?.primaryIcon}
							<img src={config?.primaryIcon} alt="action-icon" />
						{/if}
						{config?.primaryLabel}
					</button>
				{/if}
			</div>
		</div>
	</div>
{/if}

<script lang="ts">
	import type { Writable } from 'svelte/store';
	import { chatSession, isChatSessionReady, chatVisible } from '$lib/stores/chatSessionStore';
	import { endConversationLogic } from '$lib/utilities/endConversation';
	import { emailTranscript } from '$lib/apis/webchat/emailTranscript';
	import { resetIframe } from '../../utilities/draggable';
	import { datadogLogs } from '@datadog/browser-logs';
	import { startCreateTimer, stopTimer } from '$lib/utilities/disconnectConversation';
	import { floraChatEventTracking } from '$lib/utilities/analytics';

	export let modalId: Writable<string>;
	export let showModal: Writable<boolean>;
	export let chatInitialized: Writable<boolean>;
	export let initChat: () => Promise<void>;
	export let restartConversation: () => Promise<void>;

	const handleClearHistory = async () => {
		await endConversationLogic();
		await restartConversation();
		datadogLogs.logger.info(
			`[Conv-ui] Clear History Success - brand: ${$chatSession.brand} channel: ${$chatSession.channel} browser_uuid: ${$chatSession.browseruuid} conversation_id: ${$chatSession?.conversation_id}`
		);
		const unsubscribe = isChatSessionReady.subscribe(async (ready) => {
			if (ready) {
				unsubscribe();
				await initChat();
			}
		});
	};
	const handleCloseConversation = async () => {
		window.parent.postMessage({ type: 'flora-end-chat-hide-button' }, '*');
		resetIframe();
		chatVisible.update((visible) => !visible);
		await endConversationLogic();
		chatInitialized.set(false);
	};
	let email = '';
	const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
	const modalConfig = {
		disconnect_warning: {
			header: 'Are you still there?',
			message: 'Do you want to continue this conversation?',
			primaryLabel: 'Yes',
			primaryIcon: '',
			secondaryLabel: 'No',
			secondaryIcon: ''
		},
		disconnect: {
			header: 'Conversation Timeout',
			message: 'This chat has ended due to inactivity.',
			primaryLabel: '',
			primaryIcon: '',
			secondaryLabel: '',
			secondaryIcon: ''
		},
		clear_history: {
			header: 'Clear chat history?',
			message: 'This action cannot be undone.',
			primaryLabel: 'Clear',
			primaryIcon: 'modal/delete.svg',
			secondaryLabel: 'Cancel',
			secondaryIcon: ''
		},
		close_conversation: {
			header: 'Exit Chat?',
			message: 'Are you sure you want to end this conversation?',
			primaryLabel: 'Exit Chat',
			primaryIcon: 'modal/exit.svg',
			secondaryLabel: 'Cancel',
			secondaryIcon: ''
		},
		email_transcript: {
			header: 'Email Transcript',
			message: 'Where should we send this transcript?',
			primaryLabel: 'Submit',
			primaryIcon: 'modal/send.svg',
			secondaryLabel: 'Cancel',
			secondaryIcon: ''
		},
		email_api_success: {
			header: 'Successfully sent transcript.',
			message:
				"Once your chat has ended, please check your email inbox for the transcript. Note that it may take up to 15 minutes for you to receive the email. If you don't see it, check your junk and spam folders.",
			primaryLabel: '',
			primaryIcon: '',
			secondaryLabel: '',
			secondaryIcon: ''
		},
		email_api_error: {
			header: 'Oops! Something went wrong.',
			message: 'There was an issue and your transcript was not sent. Please try again later!',
			primaryLabel: '',
			primaryIcon: '',
			secondaryLabel: '',
			secondaryIcon: ''
		},
		start_api_error: {
			header: 'Oops! Something went wrong.',
			message: 'You can exit and try again later or restart and try again now.',
			primaryLabel: 'Restart Chat',
			primaryIcon: 'modal/delete.svg',
			secondaryLabel: 'Exit Chat',
			secondaryIcon: 'exitChat.svg'
		}
	};
	$: currentModalId = $modalId;
	type ModalId = keyof typeof modalConfig;
	$: config = modalConfig[currentModalId as ModalId] || modalConfig['start_api_error'];
	$: isEmailModal = currentModalId === 'email_transcript';
	$: isEmailValid = emailRegex.test(email);

	const handleConfirm = async () => {
		showModal.set(false);

		const { conversation_id, token } = $chatSession;

		switch (currentModalId) {
			case 'disconnect_warning':
				floraChatEventTracking(
					$chatSession.conversation_id || 'N/A',
					'disconnectWarning',
					'confirm'
				);

				datadogLogs.logger.info(
					`[Conv-ui] Disconnect Warning Reset - brand: ${$chatSession.brand} channel: ${$chatSession.channel} browser_uuid: ${$chatSession.browseruuid} conversation_id: ${$chatSession?.conversation_id}`
				);
				startCreateTimer($chatSession?.conversation_id);
				break;
			case 'disconnect':
				floraChatEventTracking($chatSession.conversation_id || 'N/A', 'disconnect', 'confirm');

				datadogLogs.logger.info(
					`[Conv-ui] Conversation Timeout Disconnect - brand: ${$chatSession.brand} channel: ${$chatSession.channel} browser_uuid: ${$chatSession.browseruuid} conversation_id: ${$chatSession?.conversation_id}`
				);
				await handleCloseConversation();
				break;
			case 'close_conversation':
				floraChatEventTracking(
					$chatSession.conversation_id || 'N/A',
					'closeConversation',
					'confirm'
				);

				datadogLogs.logger.info(
					`[Conv-ui] Close Conversation Pressed - brand: ${$chatSession.brand} channel: ${$chatSession.channel} browser_uuid: ${$chatSession.browseruuid} conversation_id: ${$chatSession?.conversation_id}`
				);
				await handleCloseConversation();
				break;

			case 'clear_history':
				floraChatEventTracking($chatSession.conversation_id || 'N/A', 'clearHistory', 'confirm');

				datadogLogs.logger.info(
					`[Conv-ui] Clear History Pressed - brand: ${$chatSession.brand} channel: ${$chatSession.channel} browser_uuid: ${$chatSession.browseruuid} conversation_id: ${$chatSession?.conversation_id}`
				);
				await handleClearHistory();
				break;
			case 'start_api_error':
				await handleClearHistory();
				break;

			case 'email_transcript':
				floraChatEventTracking(
					$chatSession.conversation_id || 'N/A',
					'sendEmailTranscript',
					'confirm'
				);

				datadogLogs.logger.info(
					`[Conv-ui] Email Transcript Pressed - brand: ${$chatSession.brand} channel: ${$chatSession.channel} browser_uuid: ${$chatSession.browseruuid} conversation_id: ${$chatSession?.conversation_id}`
				);
				const response = await emailTranscript(
					{
						email,
						conversation_id: conversation_id,
						brand: $chatSession?.brand,
						channel: $chatSession?.channel,
						browser_uuid: $chatSession?.browseruuid
					},
					token
				);
				if (!response) {
					modalId.update(() => 'email_api_error');
					showModal.update(() => true);
					floraChatEventTracking(
						$chatSession.conversation_id || 'N/A',
						'sendEmailTranscript',
						'failed'
					);
				} else {
					modalId.update(() => 'email_api_success');
					showModal.update(() => true);
					floraChatEventTracking(
						$chatSession.conversation_id || 'N/A',
						'sendEmailTranscript',
						'success'
					);
				}
				break;
		}
	};

	const handleCancel = async () => {
		if (currentModalId === 'start_api_error') {
			await handleCloseConversation();
		}
		if (currentModalId === 'disconnect_warning') {
			stopTimer($chatSession.conversation_id);
			await handleCloseConversation();
		}
		showModal.set(false);
	};
</script>

<style>
	.modal-overlay {
		z-index: 1000;
		position: fixed;
		inset: 0;
		background-color: rgba(0, 0, 0, 0.5);
		display: flex;
		justify-content: center;
		align-items: center;
		width: 98vw;
		height: 90vh;
		border-radius: 10px;
	}
	.mobile-modal-overlay {
		width: 100vw;
		height: 100vh;
	}
	.modal-content {
		display: flex;
		flex-direction: column;
		gap: 4px;
		background-color: #fff;
		border-radius: 8px;
		padding: 24px;
		width: 350px;
		margin: 30px;
	}

	.modal-header-container {
		display: flex;
		justify-content: space-between;
		align-items: center;
		height: 28px;
	}

	.modal-header {
		font-family: area-variable, sans-serif;
		font-weight: 700;
		font-size: 16px;
		line-height: 21px;
		letter-spacing: 0.01px;
		vertical-align: middle;
		color: #000000;
	}

	.modal-message {
		font-family: area-variable, sans-serif;
		font-weight: 400;
		font-size: 14px;
		line-height: 19px;
		letter-spacing: 0.01px;
		vertical-align: middle;
		color: #7b7b7b;
		width: 90%;
	}

	.modal-input {
		padding-top: 16px;
	}

	.modal-input input {
		width: 100%;
		height: 56px;
		padding-left: 7px;
		background: #fafafa;
		border: 1px solid #c4c4c4;
		box-sizing: border-box;
	}

	.modal-input input:focus-visible {
		outline-color: #201648;
	}

	.modal-close {
		width: 28px;
		height: 28px;
		background: #e9e9e9;
		border-radius: 30px;
		display: flex;
		justify-content: center;
		align-items: center;
		border: none;
		cursor: pointer;
	}

	.button-container {
		display: flex;
		gap: 16px;
		margin-top: 20px;
	}

	.button-container button {
		width: 160px;
		height: 41px;
		padding: 11px 24px;
		border-radius: 5px;
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 10px;
		cursor: pointer;
		border: 1px solid black;
	}

	.confirm {
		background-color: #201648;
		color: #fff;
	}

	.confirm:disabled {
		background-color: #7b7b7b;
		color: #000000;
	}
	.cancel {
		background-color: #fff;
		color: #201648;
	}
</style>
